import React, { useState } from 'react';
import { Box, Button, FormLabel } from '@material-ui/core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSelector } from 'react-redux';

import { darkPurple, white } from 'utils/color-styles';
import { ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import { DeedHeartHands } from 'components/Flow/components/DeedHeartHands';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import { usePartners } from 'entities/organization/usePartners';
import OrganizationChip from 'components/OrganizationChip';
import { Privileges } from 'utils/privileges';
import { selectUser } from 'entities/user/selectors';

import { CompanySelectInput } from './CompanySelectInput';

export const CompanyAffiliationStep = ({ send, state }: StepComponentProps): JSX.Element => {
  const user = useSelector(selectUser);
  const { data: existingPartners } = usePartners(
    undefined,
    !user?.hasPrivilege(Privileges.PARTNER_VIEW)
  );
  const [companies, setCompanies] = useState<string[]>([]);
  // Ideally we should have used a forms library for it like react-hook-form
  const [touched, setTouched] = useState(false);
  const isSubmitting = Boolean(
    typeof state.value === 'object' && 'companyAffiliation' in state.value
      ? state.value.companyAffiliation === 'submitting'
      : false
  );
  const isFailed = Boolean(
    typeof state.value === 'object' && 'companyAffiliation' in state.value
      ? state.value.companyAffiliation === 'failure'
      : false
  );

  const hasPartners = existingPartners?.length > 0;

  return (
    <ModalStep
      showProgressBar
      actions={
        <div style={{ color: darkPurple }}>
          {!state?.context?.standaloneMode && !hasPartners && (
            <Button
              color="inherit"
              type="button"
              endIcon={<ArrowForwardIcon style={{ fontSize: 16 }} />}
              style={{ marginRight: 8 }}
              onClick={(): unknown => send('SKIP')}
            >
              Skip
            </Button>
          )}
          <Button
            type="button"
            variant="contained"
            style={{ backgroundColor: darkPurple, color: white }}
            disabled={isSubmitting}
            onClick={(): void => {
              if (companies.length > 0 || hasPartners) {
                send({ type: 'SUBMIT', companies });
              } else {
                setTouched(true);
              }
            }}
          >
            {isSubmitting
              ? 'Requesting Affiliation…'
              : hasPartners && companies.length === 0
              ? 'Yes, that is correct'
              : 'Request Affiliation'}
          </Button>
        </div>
      }
      header={<DeedHeartHands />}
      headerStyles={{
        paddingTop: 16,
      }}
    >
      <Box mb={1}>
        <ModalTitle size="large">
          {hasPartners ? <>Your Current Affiliations</> : <>Are you affiliated with a company?</>}
        </ModalTitle>

        {hasPartners ? (
          <>
            <ModalTypography>
              We preselected for you companies you&apos;re already affiliated with:
            </ModalTypography>
            <Box mb={1} display="flex" flexWrap="wrap">
              {existingPartners.map((partner) => (
                <OrganizationChip key={partner.id} organization={partner} />
              ))}
            </Box>
            <ModalTypography>
              Strengthen your network and collaborations by exploring opportunities with them
              further. Looking to expand your network? Find and affiliate with other companies that
              align with your mission and values.
            </ModalTypography>
          </>
        ) : (
          <ModalTypography>
            If you have an existing relationship with a company that uses Deed for their social
            impact work, search for them below. (Select all that apply.)
          </ModalTypography>
        )}
      </Box>

      <CompanySelectInput values={companies} onChange={setCompanies} excludeExisting />
      {touched && companies.length === 0 && (
        <FormLabel error style={{ display: 'block', marginTop: 16 }}>
          At least one company must be selected
        </FormLabel>
      )}
      {isFailed && (
        <FormLabel error style={{ display: 'block', marginTop: 16 }}>
          An error occurred while trying to request affiliation. Please try again.
        </FormLabel>
      )}
    </ModalStep>
  );
};
